import { contains, isEmpty, noop } from 'underscore';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useGroupsPreviewerLayout } from '@bingads-webui-campaign-react/ad-placement-factory';
import { evaluateAdCustomizers } from '@bingads-webui-campaign/ad-customizers-next';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getDomain } from '@bingads-webui/url-util';
import { getTitlePreview, getDescPreview } from '@bingads-webui-campaign-react/ad-common';
import { defaultUrl, urlPrefix } from './defaults';
import { getPreviewStyles } from './styles';

const adType = 'ResponsiveSearchAd';

const RSAPreviewGroupType = 'responsiveSearchAd';

const AdPlacementKeys = {
  TopView: 'responsive_search_desktop',
  SideView: 'responsive_search',
  MobileView: 'responsive_search_mobile',
};

const previewLayoutConfig = {
  groupsDefinitions: [
    {
      key: 'topView',
      groupType: RSAPreviewGroupType,
      label: _TL_('Top view'),
      placementFilterPredicate: tmp => tmp.key === AdPlacementKeys.TopView,
    },
    {
      key: 'sideView',
      groupType: RSAPreviewGroupType,
      label: _TL_('Side view'),
      placementFilterPredicate: tmp => tmp.key === AdPlacementKeys.SideView,
    },
    {
      key: 'mobileView',
      groupType: RSAPreviewGroupType,
      label: _TL_('Mobile view'),
      placementFilterPredicate: tmp => tmp.key === AdPlacementKeys.MobileView,
    },
  ],
};

const ResponsiveSearchAdPreviewUAPLUnstyled = ({
  data: {
    Headlines,
    Descriptions,
    FinalUrls,
    DisplayUrl,
    Path1,
    Path2,
  },
  classes,
  disclaimerInfo,
  image,
  permissions,
}) => {
  const [headline, setHeadline] = useState('');
  const [description, setDescription] = useState('');
  const [urlPath1, setUrlPath1] = useState('');
  const [urlPath2, setUrlPath2] = useState('');

  const finalUrl = useMemo(() => ((isEmpty(FinalUrls) || contains(urlPrefix, FinalUrls[0])) ? defaultUrl : FinalUrls[0]), [FinalUrls]);
  const urlDomain = useMemo(() => getDomain(finalUrl), [finalUrl]);
  const displayUrl = useMemo(() => `${DisplayUrl || urlDomain}${urlPath1}${urlPath2}`, [DisplayUrl, urlDomain, urlPath1, urlPath2]);

  const { GroupsPreviewer } = useGroupsPreviewerLayout(previewLayoutConfig);

  const headerButtonProps = useMemo(() => ({
    onEdit: noop,
    onAddAspectRatio: noop,
    onRemoveAspectRatio: noop,
  }), []);

  useEffect(() => {
    evaluateAdCustomizers({
      adType,
      field: 'Headlines',
      text: getTitlePreview(Headlines),
    }).then((evaluatedText) => {
      setHeadline(evaluatedText);
    });
  }, [Headlines]);

  useEffect(() => {
    evaluateAdCustomizers({
      adType,
      field: 'Descriptions',
      text: getDescPreview(Descriptions, disclaimerInfo ? disclaimerInfo.LineText : ''),
    }).then((evaluatedText) => {
      setDescription(evaluatedText);
    });
  }, [Descriptions, disclaimerInfo]);

  useEffect(() => {
    evaluateAdCustomizers({
      adType,
      field: 'Path1',
      text: Path1,
    }).then((evaluatedText) => {
      setUrlPath1(evaluatedText ? `/${evaluatedText}` : '');
    });
  }, [Path1]);

  useEffect(() => {
    evaluateAdCustomizers({
      adType,
      field: 'Path2',
      text: Path2,
    }).then((evaluatedText) => {
      setUrlPath2(evaluatedText ? `/${evaluatedText}` : '');
    });
  }, [Path2]);

  const adData = useMemo(() => ({
    longHeadline: headline,
    description,
    url: finalUrl,
    displayUrl,
  }), [headline, description, finalUrl, displayUrl]);

  const decoratorsData = useMemo(() => ({
    disclaimer: disclaimerInfo,
    imageExtension: {
      image,
      enableCrop: permissions && permissions.dynamic && permissions.dynamic.RSAImageExtensionCrop,
    },
  }), [disclaimerInfo, image, permissions]);

  return (
    <div className={classes.adPreviewContainer}>
      <GroupsPreviewer
        headerButtonProps={headerButtonProps}
        adData={adData}
        decoratorsData={decoratorsData}
      />
    </div>
  );
};

ResponsiveSearchAdPreviewUAPLUnstyled.propTypes = {
  data: PropTypes.shape({
    Headlines: PropTypes.arrayOf(PropTypes.string),
    Descriptions: PropTypes.arrayOf(PropTypes.string),
    FinalUrls: PropTypes.arrayOf(PropTypes.string),
    DisplayUrl: PropTypes.string,
    Path1: PropTypes.string,
    Path2: PropTypes.string,
  }),
  classes: PropTypes.shape({
    adPreviewContainer: PropTypes.string,
  }).isRequired,
  disclaimerInfo: PropTypes.shape({}) || null,
  image: PropTypes.shape({}) || null,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      RSAImageExtensionCrop: PropTypes.bool,
    }),
  }),
};

ResponsiveSearchAdPreviewUAPLUnstyled.defaultProps = {
  data: {},
  disclaimerInfo: null,
  image: null,
  permissions: {},
};

export const ResponsiveSearchAdPreviewUAPL =
  withDefaultStyles(ResponsiveSearchAdPreviewUAPLUnstyled, getPreviewStyles);
